import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { WorkflowItem } from "@/api/models"

import { Button } from "@/components/ui/button"
import { ScrollTiltCard } from "@/components/ui/container-scroll-animation"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { SidebarInset } from "@/components/ui/sidebar"
import { Spinner } from "@/components/ui/spinner"
import { useDeleteWorkflow } from "@/features/ai-explorer/hooks/use-delete-workflow"
import { useGetSharedWorkflows } from "@/features/ai-explorer/hooks/use-get-shared-workflows"
import { useGetWorkflows } from "@/features/ai-explorer/hooks/use-get-workflows"
import { useUpsertWorkflow } from "@/features/ai-explorer/hooks/use-upsert-workflow"
import { SettingsService } from "@/services/settings-service"

import { FlowTabs } from "../components/flow-tabs"
import { FlowsAiPrompt } from "../components/flows-ai-prompt"
import { FlowsGreeting } from "../components/flows-greeting"
import { useToggleWorkflowFavorite } from "../hooks/use-toggle-workflow-favorite"
import { shouldScrollToFlowsSection } from "../utils/workflow-landing-utils"

export function WorkflowLandingPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const [workflowToDelete, setWorkflowToDelete] = useState<WorkflowItem | null>(null)
  const { data, isLoading } = useGetWorkflows()
  const { data: sharedData } = useGetSharedWorkflows()
  const toggleFavoriteMutation = useToggleWorkflowFavorite()
  const deleteWorkflowMutation = useDeleteWorkflow()
  const upsertWorkflowMutation = useUpsertWorkflow()
  const scrollContainerRef = useRef<HTMLElement>(null)

  const userName = SettingsService.getInstance().claims.name
  // Own (personal + company) flows plus flows shared with the user. Own flows win
  // on id collision so a flow you own isn't duplicated as a "shared" entry.
  const ownWorkflows = data?.workflows ?? []
  const ownIds = new Set(ownWorkflows.map((workflow) => workflow.id))
  const sharedWorkflows = (sharedData?.workflows ?? []).filter(
    (workflow) => !ownIds.has(workflow.id)
  )
  const workflows = [...ownWorkflows, ...sharedWorkflows]

  useEffect(() => {
    if (!shouldScrollToFlowsSection(location.hash, isLoading)) return

    const element = document.getElementById("flows")
    element?.scrollIntoView({ behavior: "smooth" })
  }, [isLoading, location.hash])

  const handleOpen = (workflow: WorkflowItem) => {
    void navigate(`/flows/${workflow.id}`)
  }

  const handleToggleFavorite = (workflow: WorkflowItem) => {
    toggleFavoriteMutation.mutate(workflow.id, {
      onError: () => {
        toast.error(t("toast.favoriteError"))
      },
    })
  }

  const handleDuplicate = async (workflow: WorkflowItem) => {
    try {
      const duplicatedWorkflow = await upsertWorkflowMutation.mutateAsync({
        title: `Copy of ${workflow.title}`,
        content: workflow.content,
        file_required: workflow.file_required,
        visibility: "personal",
        category: workflow.category,
        tags: workflow.tags ?? [],
        description: workflow.description,
        default_project_id: workflow.default_project_id,
      })
      toast.success(t("toast.flowDuplicated"))
      void navigate(`/flows/${duplicatedWorkflow.id}`)
    } catch {
      toast.error(t("toast.duplicateError"))
    }
  }

  const handleDelete = (workflow: WorkflowItem) => {
    setWorkflowToDelete(workflow)
  }

  const handleConfirmDelete = async () => {
    if (!workflowToDelete) return

    try {
      await deleteWorkflowMutation.mutateAsync(workflowToDelete.id)
      toast.success(t("toast.flowDeleted"))
      setWorkflowToDelete(null)
    } catch {
      toast.error(t("toast.deleteError"))
    }
  }

  if (isLoading) {
    return (
      <SidebarInset className="flex h-full w-full items-center justify-center">
        <Spinner className="h-8 w-8" />
      </SidebarInset>
    )
  }

  return (
    <SidebarInset className="relative flex h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] w-full flex-col overflow-hidden">
      {/* Background gradients */}
      <div className="from-secondary/25 via-secondary/10 to-secondary/5 pointer-events-none absolute inset-0 bg-gradient-to-tl" />
      <div className="from-secondary/20 via-secondary/10 pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_80%_80%,_var(--tw-gradient-stops))] to-transparent" />

      {/* Content */}
      <main ref={scrollContainerRef} className="relative flex-1 overflow-y-auto">
        {/* Hero section - leaves space for flows section to peek */}
        <div className="relative flex min-h-[calc(100vh-26rem)] flex-col">
          {/* Centered hero content */}
          <div className="relative flex flex-1 flex-col items-center justify-center px-6 pt-40 pb-8">
            <FlowsGreeting userName={userName} />
            <FlowsAiPrompt />
          </div>
        </div>

        {/* Flow tabs section - appears when scrolling with 3D tilt effect */}
        <div className="relative px-6 pb-32" id="flows">
          <ScrollTiltCard
            containerClassName="mx-auto max-w-5xl"
            scrollContainerRef={scrollContainerRef}
          >
            <div className="bg-card/80 rounded-xl border p-6 shadow-sm backdrop-blur-sm">
              <FlowTabs
                workflows={workflows}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                onOpen={handleOpen}
                onToggleFavorite={handleToggleFavorite}
              />
            </div>
          </ScrollTiltCard>
        </div>
      </main>

      {/* Bottom fade overlay */}
      <div className="from-background/80 pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t to-transparent" />

      {/* Delete confirmation dialog */}
      <Dialog open={!!workflowToDelete} onOpenChange={() => setWorkflowToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("deleteDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("deleteDialog.description", { title: workflowToDelete?.title })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWorkflowToDelete(null)}>
              {t("deleteDialog.cancel")}
            </Button>
            <Button
              disabled={deleteWorkflowMutation.isPending}
              variant="destructive"
              onClick={handleConfirmDelete}
            >
              {t("deleteDialog.delete")}
              {deleteWorkflowMutation.isPending && <Spinner className="ml-2" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarInset>
  )
}
